import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { CheckCircle2, XCircle, MessageSquare, Send, ShieldAlert, Clock } from "lucide-react";
import StatusPill from "@/components/aieco/StatusPill";
import { useWorkspace } from "@/context/WorkspaceContext";
import log from "@/lib/log";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RISK_TONE = (r) => (r >= 81 ? "critical" : r >= 61 ? "warning" : "success");

export default function HumanOversight() {
  const { workspaceId } = useWorkspace();
  const [reviewQueue, setReviewQueue] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [comment, setComment] = useState("");
  const [localDecisions, setLocalDecisions] = useState({});

  const fetchQueue = async () => {
    try {
      const r = await axios.get(`${API}/oversight`, { params: { workspace: workspaceId } });
      const items = r.data.items || [];
      setReviewQueue(items);
      if (items.length && !items.find((i) => i.id === activeId)) {
        setActiveId(items[0].id);
      }
    } catch (e) {
      log.warn("[oversight] fetch failed:", e?.message || e);
    }
  };

  useEffect(() => {
    fetchQueue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  const active = reviewQueue.find((r) => r.id === activeId) || reviewQueue[0];

  if (!active) {
    return (
      <div className="mx-auto max-w-[1480px] px-10 py-12 text-slate-400" data-testid="page-oversight">
        Loading review queue…
      </div>
    );
  }

  const decide = async (kind) => {
    try {
      await axios.post(`${API}/oversight/${activeId}/decision`, { kind, comment, actor: "You" });
      setComment("");
      await fetchQueue();
    } catch (e) {
      // graceful: still record locally
      const entry = { kind, comment, time: new Date().toLocaleTimeString().slice(0, 8) };
      setLocalDecisions((d) => ({ ...d, [activeId]: [...(d[activeId] || []), entry] }));
      setComment("");
    }
  };

  return (
    <div className="mx-auto max-w-[1480px] px-6 py-8 lg:px-10 lg:py-10" data-testid="page-oversight">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400">Human Oversight Center</div>
          <h1 className="mt-2 font-display text-3xl font-medium tracking-tight text-white">
            Humans, exactly where they matter.
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-400">
            Review queue, full agent trace, and one-click decisioning — every override recorded for audit.
          </p>
        </div>
        <div className="flex items-center gap-2 rounded-lg border border-white/5 bg-white/[0.02] px-3 py-1.5 text-[12px] text-slate-300" data-testid="sla-badge">
          <Clock size={13} className="text-[#5B8DEF]" />
          <span>SLA · 15m response</span>
        </div>
      </motion.div>

      <div className="mt-8 grid grid-cols-12 gap-6">
        {/* Queue */}
        <div className="col-span-12 lg:col-span-4">
          <div className="card-eco p-4">
            <div className="flex items-center justify-between border-b border-white/5 pb-3">
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Review queue</div>
              <span className="rounded-full bg-[#5B8DEF]/15 px-2 py-0.5 font-mono text-[11px] text-[#bcd2ff]">{reviewQueue.length}</span>
            </div>
            <ul className="mt-2 space-y-1">
              {reviewQueue.map((c) => {
                const isActive = c.id === activeId;
                return (
                  <li key={c.id}>
                    <button
                      onClick={() => setActiveId(c.id)}
                      data-testid={`queue-${c.id}`}
                      className={`flex w-full items-start gap-3 rounded-lg border p-3 text-left transition ${
                        isActive
                          ? "border-[#5B8DEF]/40 bg-[#5B8DEF]/[0.06]"
                          : "border-white/5 bg-white/[0.02] hover:border-white/10"
                      }`}
                    >
                      <img src={c.avatar} alt={c.applicant} className="h-9 w-9 rounded-full object-cover" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between">
                          <span className="text-[13px] text-slate-100">{c.applicant}</span>
                          <StatusPill tone={RISK_TONE(c.riskScore)} dot={false}>
                            <span className="font-mono">{c.riskScore}</span>
                          </StatusPill>
                        </div>
                        <div className="mt-0.5 truncate text-[11px] text-slate-500">{c.agent}</div>
                        <div className="mt-1 text-[11px] text-slate-500">{c.submitted}</div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        {/* Case detail */}
        <div className="col-span-12 lg:col-span-8" data-testid={`case-detail-${active.id}`}>
          <div className="card-eco p-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <img src={active.avatar} alt={active.applicant} className="h-12 w-12 rounded-full object-cover" />
                <div>
                  <div className="font-display text-xl text-white">{active.applicant}</div>
                  <div className="text-[12px] text-slate-400">
                    {active.requested} requested · {active.income} verified income
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <StatusPill tone={RISK_TONE(active.riskScore)}>
                  Priority: {active.priority}
                </StatusPill>
                <span className="rounded-lg border border-white/5 bg-white/[0.02] px-2.5 py-1 font-mono text-[11px] text-slate-300">
                  risk {active.riskScore}
                </span>
              </div>
            </div>

            <div className="mt-5 rounded-lg border border-red-500/20 bg-red-500/[0.06] p-3">
              <div className="flex items-start gap-2 text-[12px]">
                <ShieldAlert size={14} className="mt-0.5 text-red-400" />
                <div>
                  <div className="text-red-300">Policy violation</div>
                  <div className="text-slate-300">{active.violatedPolicy}</div>
                </div>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">Agent trace</div>
                <ol className="mt-2 space-y-1.5 border-l border-white/5 pl-4">
                  {active.trace.map((line, i) => (
                    <li key={i} className="relative text-[12px] text-slate-300">
                      <span className="absolute -left-[20px] top-1.5 h-1.5 w-1.5 rounded-full bg-[#5B8DEF]" />
                      <span className="font-mono text-slate-500">{line.split(" — ")[0]}</span>
                      <span className="text-slate-300"> — {line.split(" — ")[1]}</span>
                    </li>
                  ))}
                </ol>
              </div>
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">Decision history</div>
                <ul className="mt-2 space-y-1.5">
                  {active.decisionHistory.map((d, i) => (
                    <li key={i} className="rounded-lg border border-white/5 bg-white/[0.02] p-2.5 text-[12px]">
                      <div className="flex items-center justify-between">
                        <span className="text-slate-200">{d.actor}</span>
                        <span className="font-mono text-[10px] text-slate-500">{d.time}</span>
                      </div>
                      <div className="text-slate-400">{d.action} — {d.note}</div>
                    </li>
                  ))}
                  {(localDecisions[active.id] || []).map((d, i) => (
                    <li key={i} className="rounded-lg border border-[#5B8DEF]/30 bg-[#5B8DEF]/[0.08] p-2.5 text-[12px]">
                      <div className="flex items-center justify-between">
                        <span className="text-slate-100">You</span>
                        <span className="font-mono text-[10px] text-slate-500">{d.time}</span>
                      </div>
                      <div className="text-slate-300">
                        {d.kind} {d.comment ? `— ${d.comment}` : ""}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-6 rounded-xl border border-white/5 bg-white/[0.02] p-4">
              <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                <MessageSquare size={12} /> Reviewer comment
              </div>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Add justification, follow-up requests, or compliance notes…"
                rows={2}
                data-testid="reviewer-comment"
                className="mt-2 w-full resize-none rounded-lg border border-white/5 bg-[#0b1020]/60 px-3 py-2 text-[13px] text-slate-200 placeholder:text-slate-500 focus:border-[#5B8DEF]/40 focus:outline-none"
              />
              <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
                <button onClick={() => decide("Requested information")} data-testid="action-request-info" className="rounded-lg border border-white/5 bg-white/[0.02] px-3 py-1.5 text-[12px] text-slate-200 hover:border-white/10">
                  <span className="inline-flex items-center gap-1.5"><Send size={12} /> Request information</span>
                </button>
                <button onClick={() => decide("Rejected")} data-testid="action-reject" className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-1.5 text-[12px] text-red-300 hover:bg-red-500/15">
                  <span className="inline-flex items-center gap-1.5"><XCircle size={12} /> Reject</span>
                </button>
                <button onClick={() => decide("Override approved")} data-testid="action-approve" className="rounded-lg bg-[#22C55E] px-3 py-1.5 text-[12px] font-medium text-[#0b1020] hover:bg-[#3BD27A]">
                  <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={12} /> Approve override</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
